import { observable, action, computed, toJS } from "mobx";
import moment from "moment";
import _ from "lodash";
import ViewUtils from "../views/ViewUtils";

export default class PopupStore {
  constructor(http, searchStore, exporter, navigationStore) {
    this.http = http;
    this.searchStore = searchStore;
    this.exporter = exporter;
    this.navigationStore = navigationStore;

    // additional requests that have no bucket. So theses types are fetched separatly
    // instead of being included in a request response of a bucket
    this.additionalRelatedTypeRequestByTypeWithNoBucket = new Map();
    this.additionalRelatedTypeRequestByTypeWithNoBucket.set("Cube", [
      "CubeStructure",
      "Table",
    ]);

    // buckets not to fetch
    // most likely because the data is already retrieved from another call as the backend process of a type
    // may require data from multiple types
    this.bucketsNotToFetchByType = new Map();
    this.bucketsNotToFetchByType.set("Variable", [
      "Primary Variable",
      "Frameworks",
    ]);
    this.bucketsNotToFetchByType.set("Subdomain", [
      "Frameworks",
      "Cubes",
      "Member Hierarchies",
    ]);
    this.bucketsNotToFetchByType.set("VariableSet", [
      "Frameworks",
      "Cubes",
      "Subdomains",
    ]);

    this.specialFetchLimitByType = new Map();
    this.specialFetchLimitByType.set("Variable", {
      "Primary Variables": 10000,
      Variables: 10000,
    });
    this.specialFetchLimitByType.set("Cube", { default: 2000 });
    this.specialFetchLimitByType.set("MappingDefinition", { default: 1000 });

    this.relatedTypeResultInTree = ["Semantic TransformationRule"];
  }
  @observable
  prevType;

  @observable
  prevTid;

  @observable
  canRefresh = true;

  @observable
  type;

  @observable
  tid;

  @observable
  parentEntityTidByType = null;

  // validity is currently used only for subdomains and variable sets from reference date date picker
  // for others - relevant date is figured out by backend - most likely today for currently valid viewed object, last validity day for past versions, first validity date for future versions
  @observable
  validityDate = new Date();

  @observable
  entityData = {};

  @observable
  closeButton = false;

  @observable
  additionalQueryParams = observable.map();

  @observable
  searchMatrix = {};

  @observable
  displayFullProfilTips = undefined;

  @observable
  fetchingDataStatus = observable.map();

  @observable
  selectedTemplateMember = [[]];

  @observable
  displayPublishedCubesOfFramework = true;

  @observable
  showDetails = true;

  @observable
  csiDescriptionField = "";

  shouldResultsBeInTree = true;

  @action
  initAdditionalQueryParams = (type) => {
    const metadata = this.searchStore.getMetadata;
    Object.entries(metadata[type].navigationLookupBuckets).forEach(
      (entry, index) => {
        this.additionalQueryParams.set(entry[0], {
          currentPage: 1,
          searchExpression: "",
        });
        if (this.specialFetchLimitByType.has(entry[1])) {
          const specialLimit = this.specialFetchLimitByType.get(entry[1]);
          this.additionalQueryParams.get(entry[0]).perPage = !!specialLimit[
            entry[0]
          ]
            ? specialLimit[entry[0]]
            : specialLimit["default"] ?? 1500;
        } else {
          this.additionalQueryParams.get(entry[0]).perPage = 100;
        }
      }
    );
  };

  @action
  setParentEntityTidByType = (parentEntityTidByType) => {
    this.parentEntityTidByType = parentEntityTidByType;
  };

  @action
  setCsiDescriptionField = (csiDescriptionField) => {
    this.csiDescriptionField = csiDescriptionField;
  };

  @action
  clearEntityData = () => {
    this.entityData = {};
    this.showDetails = false;
  };

  @action
  setcloseButton = (closeButton) => {
    this.closeButton = closeButton;
  };

  @action
  setCanRefresh = (canRefresh) => {
    this.canRefresh = canRefresh;
  };

  @action
  setTid = (tid) => {
    this.tid = tid;
    this.canRefresh = true;
  };

  @action
  setPrevTid = (tid) => {
    this.prevTid = tid;
  };

  @action
  setPrevType = (type) => {
    this.prevType = type;
  };

  @action
  setDisplayPublishedCubesOfFramework = (value) => {
    this.displayPublishedCubesOfFramework = !!value;
  };

  @action
  setType = (type) => {
    this.type = type;
  };

  @action
  setValidityDate = (date) => {
    this.validityDate = new Date(date);
    this.resetAdditionalQueryParams();
    this.clearEntityData();
    if (this.type && this.tid) this.fetchEntityDetails(this.type, this.tid);
  };

  @action
  resetAdditionalQueryParams = () => {
    this.additionalQueryParams.forEach((qp) => {
      qp.currentPage = 1;
      qp.searchExpression = "";
    });
  };

  @action
  fetchEntityDetails = async (
    type = this.type,
    tid = this.tid,
    relatedTypeForSingleRefetch,
    callbackWithEntityData
  ) => {
    const queryParamsForBuckets = {};
    const { getMetadata } = this.searchStore;

    const typesWithSpecialRepresentation = new Set()
      .add("Cube")
      .add("Member")
      .add("CubeLink")
      .add("Combination")
      .add("Subdomain")
      .add("VariableSet")
      .add("CubeHierarchy")
      .add("MemberHierarchy")
      .add("MappingDefinition")
      .add("FacetRepresentation")
      .add("Variable");

    const typesWithDeepItemsComparaison = new Set()
      .add("VariableSet")
      .add("Framework")
      .add("Variable")
      .add("Member")
      .add("Subdomain")
      .add("CubeLink")
      .add("CubeStructureItemLink")
      .add("MemberHierarchy");

    let bucketNames = [];
    if (!!relatedTypeForSingleRefetch) {
      bucketNames.push(relatedTypeForSingleRefetch);
    } else {
      bucketNames = Object.keys(getMetadata[type].navigationLookupBuckets);
    }

    if (bucketNames.length > 0) {
      const singularTypes = !!relatedTypeForSingleRefetch
        ? Object.entries(getMetadata[type].navigationLookupBuckets)
            .filter((entry) => entry[0] === relatedTypeForSingleRefetch)
            .map((entry) => entry[1])
        : Object.values(getMetadata[type].navigationLookupBuckets);

      bucketNames.forEach((t, index) => {
        let universalFilter = null;
        if (
          this.additionalQueryParams.get(t) &&
          this.additionalQueryParams.get(t).searchExpression !== ""
        ) {
          universalFilter = {
            filterType: "STRING_FIELD",
            consideredFields: ["name", "code"],
            filter: {
              // TODO fill filtering string from some dedicated search bar
              contains: this.additionalQueryParams.get(t).searchExpression,
            },
          };
          // TODO default sorting defined on backend, no need to fill unless required
          // sort: [
          //   { column: "variableId", direction: "ASC" }
          // ]
        }
        queryParamsForBuckets[t] = {
          filter: {
            type: singularTypes[index],
            universalFilter: universalFilter,
          },
          limit: {
            offset:
              (this.getAdditionalQueryParams[t].currentPage - 1) *
              this.getAdditionalQueryParams[t].perPage,
            // related cubes have specific representation - all cubes filterable right in the browser with no paging, we expect up to couple of hundreds cubes at most => it is ok to use always page size 1000
            limit: this.getAdditionalQueryParams[t].perPage,
          },
        };
      });

      let bucketNamesToGet = [];
      if (!!relatedTypeForSingleRefetch) {
        bucketNamesToGet.push(relatedTypeForSingleRefetch);
      } else {
        // Filter buckets to prevent duplicate calls to backend as it is the same type and data is already filled.
        // Only perform a call for "Primary" or "Foreign" if the type is not called at least one.
        // Example: ['Members', 'Member Hierarchies', 'Primary Variables', 'Variables', 'Subdomains', 'FacetEnumerations'] -> call for "Primary Variables" not performed
        // Example: ['Members', 'Member Hierarchies', 'Primary Variables',              'Subdomains', 'FacetEnumerations'] -> call for "Primary Variables" performed
        bucketNamesToGet = Object.keys(
          getMetadata[type].navigationLookupBuckets
        ).filter((key, index, array) => {
          if (
            ((key.includes("Primary") || key.includes("Foreign")) &&
              array.includes(key.split(" ")[1])) ||
            ((key.includes("Primary Member Links") ||
              key.includes("Foreign Member Links")) &&
              array.includes("Member Links"))
          ) {
            return false;
          }
          return true;
        });
      }

      const buildRequestParamsAndFetchEntity = (
        relatedType,
        hasBucketToFetch = true
      ) => {
        let fetchProps = {
          type: type,
          tid: tid,

          // subdomains and variable sets use reference date date picker,
          // for others - backend will choose the relevant date based on validity of the object (today for currently valid, end of validity for past, start of validity for future)
          validOnForRelatedObjects:
            type !== "Subdomain" && type !== "VariableSet"
              ? null
              : this.validityDate
              ? moment(this.validityDate, "YYYY-MM-DD").format("YYYY-MM-DD")
              : null,

          cubeTypes: this.searchStore.getSelectedCubeTypes,
          relatedType: hasBucketToFetch
            ? queryParamsForBuckets[relatedType].filter.type
            : relatedType,
          queryParamsForBuckets: queryParamsForBuckets,
          resultsInTree:
            (typesWithSpecialRepresentation.has(type) &&
              this.shouldResultsBeInTree) ||
            this.relatedTypeResultInTree.includes(relatedType),
          resultsInBuckets: hasBucketToFetch,
        };
        if (this.parentEntityTidByType) {
          fetchProps.parentEntityTidByType = this.parentEntityTidByType;
        }
        this.shouldResultsBeInTree = true;
        this.fetchingDataStatus.set(relatedType, true);
        this.http.fetchEntityDetails(fetchProps, true).then((data) => {
          this.processAndAssignReceivedData(
            data,
            type,
            relatedType,
            queryParamsForBuckets,
            typesWithDeepItemsComparaison,
            callbackWithEntityData
          );
        });
      };

      if (
        this.additionalRelatedTypeRequestByTypeWithNoBucket.has(type) &&
        !relatedTypeForSingleRefetch
      ) {
        this.additionalRelatedTypeRequestByTypeWithNoBucket
          .get(type)
          .forEach((type) => buildRequestParamsAndFetchEntity(type, false));
      }
      bucketNamesToGet.forEach((bucketName) => {
        // fetch only necessary buckets
        if (
          !this.bucketsNotToFetchByType.has(type) ||
          !this.bucketsNotToFetchByType.get(type).includes(bucketName)
        ) {
          buildRequestParamsAndFetchEntity(bucketName);
        }
      });
    } else {
      this.fetchingDataStatus.set(type, true);
      const fetchProps = {
        type: type,
        tid: tid,
        // subdomains and variable sets use reference date date picker,
        // for others - backend will choose the relevant date based on validity of the object (today for currently valid, end of validity for past, start of validity for future)
        validOnForRelatedObjects:
          type !== "Subdomain" && type !== "VariableSet"
            ? null
            : this.validityDate
            ? moment(this.validityDate, "YYYY-MM-DD").format("YYYY-MM-DD")
            : null,
        cubeTypes: [],
        relatedType: type,
        queryParamsForBuckets,
        resultsInTree:
          typesWithSpecialRepresentation.has(type) ||
          this.relatedTypeResultInTree.includes(type),
        resultsInBuckets: true,
      };

      this.http.fetchEntityDetails(fetchProps, true).then((response) => {
        const data = toJS(response);
        if (
          data &&
          data.item &&
          data.item.navigationLookupResult &&
          data.item.navigationLookupResult.navigationLookupBuckets &&
          data.item.navigationLookupResult.navigationLookupBuckets.Cubes &&
          data.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects
        ) {
          data.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects =
            data.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects
              .slice()
              .sort((a, b) => {
                return a.code === b.code
                  ? moment(a.validFrom).isBefore(moment(b.validFrom))
                    ? 1
                    : -1
                  : a.code < b.code;
              })
              .filter(
                (item, index, self) =>
                  index === self.findIndex((t) => t.code === item.code)
              );
          data.item.navigationLookupResult.navigationLookupBuckets.Cubes.totalCountOfObjects =
            data.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects.length;
        }
        this.entityData = data;
        this.navigationStore.setTabsInfos(data);
        this.fetchingDataStatus.set(type, false);

        if (callbackWithEntityData) {
          callbackWithEntityData(this.entityData);
        }
      });
    }
  };

  // used for seperata page loading
  processAndAssignReceivedData(
    data,
    type,
    relatedType,
    queryParamsForBuckets,
    typesWithDeepItemsComparaison,
    callbackWithEntityData
  ) {
    let tempEntityData = toJS(this.entityData);
    if (!tempEntityData.item) tempEntityData = toJS(data);
    tempEntityData.params = data.params
    this.setDefaultValues(tempEntityData);

    if (
      data &&
      data.item &&
      data.item.navigationLookupResult &&
      data.item.navigationLookupResult.navigationLookupBuckets &&
      data.item.navigationLookupResult.navigationLookupBuckets.Cubes &&
      data.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects
        ?.length > 0
    ) {
      data.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects =
        data.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects
          .slice()
          .sort((a, b) => {
            return a.code === b.code
              ? moment(a.validFrom).isBefore(moment(b.validFrom))
                ? 1
                : -1
              : a.code < b.code;
          })
          .filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.code === item.code)
          );
      data.item.navigationLookupResult.navigationLookupBuckets.Cubes.totalCountOfObjects =
        data.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects.length;
      tempEntityData.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects =
        toJS(
          data.item.navigationLookupResult.navigationLookupBuckets.Cubes.objects
        );
    }
    if (
      data &&
      data.item &&
      data.item.navigationLookupResult &&
      data.item.navigationLookupResult.navigationLookupBuckets &&
      data.item.navigationLookupResult.navigationLookupBuckets[relatedType]
    ) {
      const bucketTotalCountOfObjectsBeforeMerge =
        tempEntityData?.item?.navigationLookupResult?.navigationLookupBuckets[
          relatedType
        ]?.totalCountOfObjects ?? 0;
      const bucketTotalCountOfObjectsOfNewRequest =
        data.item.navigationLookupResult.navigationLookupBuckets[relatedType]
          .totalCountOfObjects;

      const type = queryParamsForBuckets[relatedType].filter.type;
      if (
        this.isAnyfilteringApplied(queryParamsForBuckets, type, relatedType)
      ) {
        tempEntityData.item.navigationLookupResult.navigationLookupBuckets[
          relatedType
        ] = toJS(
          data.item.navigationLookupResult.navigationLookupBuckets[relatedType]
        );
      } else {
        tempEntityData.item.navigationLookupResult.navigationLookupBuckets[
          relatedType
        ] = _.merge(
          data.item.navigationLookupResult.navigationLookupBuckets[relatedType],
          tempEntityData.item.navigationLookupResult.navigationLookupBuckets[
            relatedType
          ]
        );
      }

      tempEntityData.item.navigationLookupResult.navigationLookupBuckets[
        relatedType
      ].totalCountOfObjects =
        bucketTotalCountOfObjectsOfNewRequest === 0
          ? bucketTotalCountOfObjectsBeforeMerge
          : bucketTotalCountOfObjectsOfNewRequest;
    }

    if (
      data &&
      data.item &&
      data.item.navigationLookupResult &&
      data.item.navigationLookupResult.navigationLookupRootItem &&
      data.item.navigationLookupResult.navigationLookupRootItem.items
    ) {
      tempEntityData.item.navigationLookupResult.navigationLookupRootItem.items =
        tempEntityData.item.navigationLookupResult.navigationLookupRootItem.items.concat(
          data.item.navigationLookupResult.navigationLookupRootItem.items
        );

      this.setDefaultValues(tempEntityData);

      if (typesWithDeepItemsComparaison.has(type)) {
        tempEntityData.item.navigationLookupResult.navigationLookupRootItem.items =
          _.uniqWith(
            tempEntityData.item.navigationLookupResult.navigationLookupRootItem
              .items,
            _.isEqual
          );
      } else {
        function _uniqTid(items) {
          return items.filter(function ({ object }, key) {
            return !this.has((key = object.tid)) && this.add(key);
          }, new Set());
        }

        tempEntityData.item.navigationLookupResult.navigationLookupRootItem.items =
          _uniqTid(
            tempEntityData.item.navigationLookupResult.navigationLookupRootItem
              .items
          );
      }

      tempEntityData.item.navigationLookupResult.navigationLookupRootItem.totalCountOfItems =
        tempEntityData.item.navigationLookupResult.navigationLookupRootItem.items.length;
    }

    if (!!data?.item?.otherInformation) {
      Object.keys(data.item.otherInformation).forEach((key) => {
        tempEntityData.item.otherInformation[key] =
          tempEntityData.item.otherInformation?.[key] ?? [];

        tempEntityData.item.otherInformation[key] =
          tempEntityData.item.otherInformation[key].concat(
            data.item.otherInformation[key]
          );

        // specific filtering for heavy data structures to increase performance
        const distinctFunctionByStrucutureType = {
          VariableTree: (a, b) => a?.variable?.tid === b?.variable?.tid,
          memberHierarchyTree: (a, b) =>
            a?.member?.tid === b?.member?.tid &&
            a?.sddMemberHierarchy?.tid === b?.sddMemberHierarchy?.tid,
          mainMemberHierarchyTree: (a, b) =>
            a?.member?.tid === b?.member?.tid &&
            a?.sddMemberHierarchy?.tid === b?.sddMemberHierarchy?.tid,
          dpmTemplateCombinations: (a, b) => a?.tid === b?.tid,
        };
        tempEntityData.item.otherInformation[key] = _.uniqWith(
          tempEntityData.item.otherInformation[key],
          !!distinctFunctionByStrucutureType[key]
            ? distinctFunctionByStrucutureType[key]
            : _.isEqual
        );

        tempEntityData.item.otherInformation[key] =
          tempEntityData.item.otherInformation[key].filter(
            (otherInfo) => !!otherInfo && Object.keys(otherInfo).length > 0
          );
      });
    }
    this.entityData = JSON.parse(JSON.stringify(tempEntityData));
    this.navigationStore.setTabsInfos(this.entityData);
    this.fetchingDataStatus.set(relatedType, false);

    if (callbackWithEntityData) {
      callbackWithEntityData(this.entityData);
    }
    this.setDisplayFullProfilTips(
      ViewUtils.hasNonEmptyBuckets(
        this.entityData?.item?.navigationLookupResult?.navigationLookupBuckets
      )
    );
  }

  isAnyfilteringApplied(queryParamsForBuckets, type, relatedType) {
    let defaultLimitForTypeAndRelatedType = 100;
    if (this.specialFetchLimitByType.has(type)) {
      const specialLimit = this.specialFetchLimitByType.get(type);
      defaultLimitForTypeAndRelatedType = !!specialLimit[relatedType]
        ? specialLimit[relatedType]
        : specialLimit["default"];
    }

    return (
      !!queryParamsForBuckets[relatedType].filter.universalFilter ||
      queryParamsForBuckets[relatedType].limit.offset !== 0 ||
      queryParamsForBuckets[relatedType].limit.limit !==
        defaultLimitForTypeAndRelatedType
    );
  }

  setDefaultValues = (tempEntityData) => {
    if (!tempEntityData?.item) {
      tempEntityData.item = {};
    } else if (!tempEntityData.item?.navigationLookupResult) {
      tempEntityData.item.navigationLookupResult = {};
    } else if (
      !tempEntityData.item.navigationLookupResult?.navigationLookupBuckets
    ) {
      tempEntityData.item.navigationLookupResult.navigationLookupBuckets = {};
    } else if (!tempEntityData.item?.navigationLookupResult) {
      tempEntityData.item.navigationLookupResult = {};
    } else if (
      !tempEntityData.item.navigationLookupResult?.navigationLookupRootItem
    ) {
      tempEntityData.item.navigationLookupResult.navigationLookupRootItem = {};
    } else if (
      !tempEntityData.item.navigationLookupResult?.navigationLookupRootItem
        ?.items
    ) {
      tempEntityData.item.navigationLookupResult.navigationLookupRootItem.items =
        [];
    } else if (!tempEntityData.item?.otherInformation) {
      tempEntityData.item.otherInformation = {};
    }
  };

  @action
  fetchEntityDetailsWithoutCallback = (type, tid, relatedType) => {
    const params = {
      type: type,
      tid: tid,
      // this method is currently used only from combination view - for obtaining counterpart representation, but the logic can be generic
      // subdomains and variable sets use reference date date picker,
      // for others - backend will choose the relevant date based on validity of the object (today for currently valid, end of validity for past, start of validity for future)
      validOnForRelatedObjects:
        type !== "Subdomain" && type !== "VariableSet"
          ? null
          : this.validityDate
          ? new Date()
          : null,
      cubeTypes: this.searchStore.getSelectedCubeTypes,
      resultsInTree: true,
      resultsInBuckets: true,
    };

    if (!!relatedType) {
      params.relatedType = relatedType;
      this.fetchingDataStatus.set(relatedType, true);
    }
    const promise = this.http.fetchEntityDetails(params, true);
    if (!!relatedType) {
      this.fetchingDataStatus.set(relatedType, false);
    }
    this.navigationStore.setTabsInfos(promise);
    return promise;
  };

  @action
  setCurrentPage = (type, newPageValue) => {
    const typeParams = this.getAdditionalQueryParams[type];
    typeParams.currentPage = newPageValue;
    this.additionalQueryParams.set(type, typeParams);

    if (type === "Combinations") {
      this.shouldResultsBeInTree = false;
    }
  };

  @action
  setPerPage = (type, newPerPageValue) => {
    const typeParams = this.getAdditionalQueryParams[type];
    typeParams.perPage = newPerPageValue;
    this.additionalQueryParams.set(type, typeParams);

    if (type === "Combinations") {
      this.shouldResultsBeInTree = false;
    }
  };

  @action
  setSearchExpression = (type, searchExpression) => {
    const typeParams = this.getAdditionalQueryParams[type];
    typeParams.searchExpression = searchExpression;
    this.additionalQueryParams.set(type, typeParams);

    if (type === "Combinations") {
      this.shouldResultsBeInTree = false;
    }
  };

  @action
  setSearchMatrix = (key, value) => {
    this.searchMatrix[key] = value;
  };

  @action
  setSelectedTemplateMembers = (item, itemIndex, index) => {
    // Those loop are made because MobX array can genrerate error when accessing a specific index
    // Mobx  arrays don't work well and can crash if there is a "hole" in the Array before the specific index we modify
    while (this.selectedTemplateMember.length <= itemIndex) {
      this.selectedTemplateMember.push([]);
    }

    while (this.selectedTemplateMember[itemIndex].length <= index) {
      this.selectedTemplateMember[itemIndex].push(undefined);
    }

    this.selectedTemplateMember[itemIndex][index] = item;
  };

  @computed
  get shouldDisplayPublishedCubesOfFramework() {
    return this.displayPublishedCubesOfFramework;
  }

  @computed
  get getValidityDate() {
    return this.validityDate;
  }

  @computed
  get getSearchMatrix() {
    return this.searchMatrix;
  }

  @computed
  get getEntityData() {
    return toJS(this.entityData);
  }

  @computed
  get getAdditionalQueryParams() {
    return toJS(this.additionalQueryParams);
  }

  getPerPage = (type) => {
    return computed(() => this.additionalQueryParams[type].perPage).get();
  };

  getCurrentPage = (type) => {
    return computed(() => this.additionalQueryParams[type].currentPage).get();
  };

  getSearchExpression = (type) => {
    return computed(
      () => this.additionalQueryParams[type].searchExpression
    ).get();
  };

  applyFiltersWithCubeHierarchy = (cubeHierarchiesByCubeTypes, searchExpr) => {
    return (
      cubeHierarchiesByCubeTypes &&
      cubeHierarchiesByCubeTypes.map((cubeType) => {
        return {
          ...cubeType,
          items: cubeType.items.reduce((prevResults, currentCubeHierarchy) => {
            const hierarchy = currentCubeHierarchy.sddCubeHierarchy;

            const currentHierarchyContainsSearchExpr =
              hierarchy.name.toLowerCase().includes(searchExpr.toLowerCase()) ||
              hierarchy.code
                .toLowerCase()
                .includes(searchExpr.toLocaleLowerCase());

            if (currentHierarchyContainsSearchExpr) {
              return [...prevResults, currentCubeHierarchy];
            } else {
              const filteredCubes = this.applyFilters(
                currentCubeHierarchy.sddCubes,
                searchExpr
              );
              if (filteredCubes.length === 0) {
                return prevResults; // Do not include current hierarchy into results
              } else {
                return [
                  ...prevResults,
                  {
                    ...currentCubeHierarchy,
                    sddCubes: filteredCubes,
                  },
                ];
              }
            }
          }, []),
        };
      })
    );
  };

  applyFilters = (items, searchExpr) => {
    return computed(() => {
      return items.filter(
        (item) =>
          item.name.toLowerCase().includes(searchExpr.toLowerCase()) ||
          item.code.toLowerCase().includes(searchExpr.toLocaleLowerCase())
      );
    }).get();
  };

  @computed
  get getSelectedTemplateMembers() {
    return this.selectedTemplateMember;
  }

  @action
  exportMappingDefinition = (tids) => {
    this.exporter.exportMappingDefinitions(tids);
  };

  @action
  exportVariableMembers = (
    entityData,
    exactSearch = false,
    frameworkfilter = null
  ) => {
    const entityType = entityData.object && entityData.object.type;
    const fileName = this.exporter.generateFileName(
      entityType + "Members",
      "xlsx"
    );
    const selectedFrameworks =
      frameworkfilter && frameworkfilter.tid
        ? frameworkfilter.tid
        : frameworkfilter !== "all"
        ? this.searchStore.findSelectedFrameworks()
        : [];
    let url = this.exporter.str_URL_BASE + "excel/members?format=xlsx";
    url += "&tid=" + entityData.object.tid;
    url += "&type=" + entityData.object.type;
    url += "&exactSearch=" + exactSearch;
    url += "&selectedFrameworks=" + selectedFrameworks;
    url += `&cubeTypes=` + this.searchStore.getSelectedCubeTypes || [];
    this.exporter.sendExportRequest(url, fileName, "xlsx");
  };

  @action
  groupCubesByValidity = (array, key) => {
    return array.reduce((result, currentValue) => {
      if (!result[currentValue[key]]) result[currentValue[key]] = [];

      if (result[currentValue[key]].length > 0) {
        if (result[currentValue[key]][0].validFrom < currentValue.validFrom)
          result[currentValue[key]][0] = currentValue;
      } else result[currentValue[key]].push(currentValue);
      return result;
    }, {});
  };

  @action
  setEntityDetails(entityDetails) {
    this.entityData = entityDetails;
  }
  @action
  setDisplayFullProfilTips(value) {
    this.displayFullProfilTips = value;
  }
  @computed
  get getDisplayFullProfilTips() {
    return this.displayFullProfilTips;
  }
}
